import { FC, useCallback, useEffect, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import "./i18n.config";
import Routes from "routes/routes";
import { Layout } from "components";
import { AppStyles } from "App.styled";
import { telegramAuth } from "api/auth";
import { getUsersCompanyInfo } from "api/company";
import { UserStore, WalletStore } from "stores";
import { getWalletsList } from "api/wallet";
import { socket } from "api/socket";
import { getRequestedForms } from "api/form";
import { getFntClaimInfo } from "api/fntClaim";
import { parseJwt } from "helpers/funcs";
import type { WalletsListInterface } from "helpers/types";
import type { TelegramAuthCredentials } from "api/types/user";

const App: FC = () => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const {
    isAuthenticated,
    user: { type: userType, email },
    setAuthenticated,
    turnOnMFA,
  } = UserStore;
  const { walletsList, setWalletsList } = WalletStore;

  const isByBit = useMemo<boolean>(
    () => pathname.includes("futures"),
    [pathname]
  );

  useEffect(() => {
    if (isAuthenticated) {
      socket.connect();
      getWalletsList();
      getRequestedForms();
      if (process.env.REACT_APP_ENVIRONMENT === "dev") {
        getFntClaimInfo();
      }
      if (userType === "COMPANY_USER" || userType === "COMPANY") {
        getUsersCompanyInfo();
      }
    }

    if (!isAuthenticated) {
      socket.disconnect();
    }

    return () => {
      socket.disconnect();
    };
  }, [isAuthenticated, userType, email]);

  const balanceChange = useCallback(
    (data: WalletsListInterface) => {
      if (
        walletsList &&
        !walletsList.find(
          ({ currencyName }) => currencyName === data.currencyName
        )
      ) {
        const newWalletsList = [...walletsList, data];
        setWalletsList(newWalletsList);
        return;
      }

      const newWalletsList = walletsList
        ? [...walletsList].reduce(
            (acc: WalletsListInterface[], wallet: WalletsListInterface) => {
              if (data.currencyName === wallet.currencyName) {
                wallet.balance = data.balance;
              }
              acc.push(wallet);
              return acc;
            },
            []
          )
        : [data];

      setWalletsList(newWalletsList);
    },
    [walletsList, setWalletsList]
  );

  useEffect(() => {
    socket.on("connect", () => {
      console.log("WebSocket connected");
    });
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      const decodedJwt = parseJwt(token);
      const now = Math.floor(Date.now() / 1000);
      const isAuth = decodedJwt.exp > now;

      if (
        decodedJwt.scope &&
        Array.isArray(decodedJwt.scope) &&
        decodedJwt.scope.includes("MFA")
      ) {
        turnOnMFA();
      }

      if (isAuth) {
        setAuthenticated();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    socket.on("BALANCE_CHANGED", balanceChange);

    return () => {
      socket.off("BALANCE_CHANGED", balanceChange);
    };
  }, [balanceChange]);

  // useEffect(() => {
  //   // Fix for mobile render view
  //   const refreshStyles = () => {
  //     const element = document.body;

  //     element.style.display = "none";
  //     // @typescript-eslint/no-unused-expressions
  //     // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  //     element.offsetHeight; // Trigger reflow
  //     element.style.display = "";
  //   };

  //   refreshStyles();
  // }, []);

  useEffect(() => {
    // Check for Telegram WebApp API
    if (window.Telegram?.WebApp) {
      const webApp = window.Telegram.WebApp;
      const webAppData = webApp.initData;

      if (webAppData) {
        const referralCode = searchParams.get("referralCode");
        const credentials: TelegramAuthCredentials = {
          onAuthData: webAppData,
        };
        if (referralCode) {
          credentials.referralCode = referralCode;
        }
        telegramAuth(credentials);
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout isDarkTheme={isByBit}>
      <AppStyles $isDarkTheme={isByBit} />
      <Routes />
    </Layout>
  );
};

export default observer(App);
